import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { notificationIconMap } from '@aibees/elephant/utils';
import {
  useBootstrapApiBase,
  useCreatePresetMutationApi,
} from '@aibees/shared/api';
import { FormOnSubmit } from '@aibees/shared/components';
import { useAdvanceTableContext } from '@aibees/shared/features/advance-table/context';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { baseQueryClient } from '@aibees/shared/http/base-query-client';
import { useWorkspace } from '@aibees/shared/workspace';
import { Dialog, Fade } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import { CreateActionBulkDialog } from '../create-action-bulk-dialog';
import { SearchTableHeaderButton } from '../search-table-header-button';
import { type SaveSearchButtonProps as Props } from './types';

export const SaveSearchButton = memo((props: Props) => {
  const { query, presetType, compact } = props;

  const api = useCreatePresetMutationApi();
  const { params } = useSearchQueryFilter();
  const notify = useNotify();
  const { table } = useAdvanceTableContext();
  const ids = useMemo(
    () => table?.selection?.included ?? [],
    [table?.selection?.included]
  );
  const saveDialogState = useBoolean(false);
  const grouping = useLabelGroupingStatus();
  const workspaceId = useWorkspace()?.id;

  const handleOpenDialog = useCallback(() => {
    saveDialogState.onTrue();
  }, [saveDialogState]);

  const handleCloseDialog = useCallback(() => {
    saveDialogState.onFalse();
  }, [saveDialogState]);

  const handleSubmit: FormOnSubmit<any> = useCallback(
    (data, e) => {
      if (data.name === '') {
        return;
      }
      e?.preventDefault();

      const key = Date.now().toString();

      notify.progress(
        'Save search is in progress',
        `Saving your search...`,
        key,
        { icon: notificationIconMap.SaveSearch }
      );

      api
        .mutateAsync({
          ...data,
          name: data.name,
          type: presetType,
          value: query || params,
          visibility: data.visibility,
        })
        .then(() => {
          notify.closeSnackbar(key);
          notify.complete(
            'Your Save search process is complete',
            `Your search has been saved successfully.`,
            random(1, 101),
            { icon: notificationIconMap.SaveSearch }
          );
          baseQueryClient.invalidateQueries([
            useBootstrapApiBase.name,
            workspaceId,
          ]);
          saveDialogState.onFalse();
          e?.stopPropagation();
        })
        .finally(() => {
          e?.stopPropagation();
        });
    },
    [
      api,
      ids.length,
      notify,
      params,
      presetType,
      query,
      saveDialogState,
      workspaceId,
    ]
  );

  return (
    <>
      <SearchTableHeaderButton
        text="Save Search"
        icon="bookmark"
        variant="text"
        color="default"
        compact={compact}
        onClick={handleOpenDialog}
      />

      <Dialog
        open={saveDialogState.value}
        TransitionComponent={Fade}
        onClose={handleCloseDialog}
        maxWidth="sm"
      >
        <CreateActionBulkDialog
          state={saveDialogState}
          icon="bookmark"
          title="Save Search"
          inputTitle="Name:"
          placeholder="Enter save search name..."
          submitTitle="Save"
          grouping={grouping}
          onClick={handleSubmit}
        />
      </Dialog>
    </>
  );
});

SaveSearchButton.displayName = 'SaveSearchButton';
